import { markLoggedOut } from '../lib/equalPswSso'

export interface LoginResponse {
  id: number
  name: string
  email: string
  role: string
  /** R2-MFA: sessione ristretta, solo configurazione del codice di accesso. */
  mfaSetupRequired?: boolean
}

/**
 * F-040 / REQ-032-002. What `POST /api/auth/login` returns instead of a session when the account
 * has an active second factor (`AuthController.Login`'s `MfaLoginGateDecision.ChallengeRequired`
 * branch) — a single-use, server-stored proof, redeemable for 5 minutes via `verifyMfaChallenge`.
 * `challengeToken` lives only in React state for as long as the second login step is on screen —
 * never in the URL, `localStorage`, or `sessionStorage` — so it does not survive a page reload
 * (by design: the brief calls a still-usable proof after a reload a bigger risk than forcing a
 * fresh login).
 */
export interface MfaChallenge {
  mfaRequired: true
  challengeToken: string
  expiresAt: string
}

export type LoginResult = LoginResponse | MfaChallenge

export function isMfaChallenge(result: LoginResult): result is MfaChallenge {
  return (result as MfaChallenge).mfaRequired === true
}

async function fetchCsrfToken(): Promise<string> {
  const res = await fetch('/api/antiforgery/token', { credentials: 'include' })
  const data = (await res.json()) as { token: string }
  return data.token
}

export async function login(email: string, password: string, rememberMe = false): Promise<LoginResult> {
  const csrfToken = await fetchCsrfToken()
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-XSRF-TOKEN': csrfToken,
    },
    credentials: 'include',
    body: JSON.stringify({ email, password, rememberMe }),
  })

  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(err.detail ?? 'Login failed')
  }

  return (await res.json()) as LoginResult
}

/**
 * F-040 / REQ-032-002. Redeems a login challenge (`POST /api/auth/mfa/verify`) with either a
 * 6-digit TOTP code or a recovery code — `MfaService.TryVerifyCodeAsync` tries both against the
 * same `code` field, so the caller does not need to say which kind it is. Throws `MfaVerifyError`
 * (not a plain `Error`) so the caller can branch on the backend's stable `code` — a wrong code
 * (`MFA_INVALID_CODE`) must keep the same challenge on screen for another try (confirmed by
 * reading `MfaService.VerifyLoginChallengeAsync`: it only calls `challenge.MarkUsed()` on success,
 * never on a wrong code), while an invalid/expired/already-used challenge (`INVALID_CHALLENGE`) or
 * a locked/deactivated account has no way forward except back to step one.
 */
export class MfaVerifyError extends Error {
  readonly code: string

  constructor(message: string, code: string) {
    super(message)
    this.name = 'MfaVerifyError'
    this.code = code
  }
}

export async function verifyMfaChallenge(challengeToken: string, code: string, rememberMe = false): Promise<LoginResponse> {
  const csrfToken = await fetchCsrfToken()
  const res = await fetch('/api/auth/mfa/verify', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-XSRF-TOKEN': csrfToken,
    },
    credentials: 'include',
    body: JSON.stringify({ challengeToken, code, rememberMe }),
  })

  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string; code?: string }
    throw new MfaVerifyError(err.detail ?? 'Codice non valido o scaduto.', err.code ?? 'UNKNOWN')
  }

  return (await res.json()) as LoginResponse
}

/** Errore dell'accesso con Equal PSW con il codice stabile del backend (`sso_invalid`, `sso_no_user`, `sso_unavailable`, `RATE_LIMITED`). */
export class SsoLoginError extends Error {
  readonly code: string

  constructor(message: string, code: string) {
    super(message)
    this.name = 'SsoLoginError'
    this.code = code
  }
}

/**
 * Accesso con Equal PSW: manda l'asserzione ricevuta dal vault a `POST /api/auth/sso`. Risponde come
 * `login()` (sessione aperta, oppure la sfida del secondo fattore se l'account ce l'ha attivo).
 */
export async function ssoLogin(assertion: string): Promise<LoginResult> {
  let res: Response
  try {
    const csrfToken = await fetchCsrfToken()
    res = await fetch('/api/auth/sso', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': csrfToken,
      },
      credentials: 'include',
      body: JSON.stringify({ assertion }),
    })
  } catch {
    throw new SsoLoginError('CRM non raggiungibile.', 'sso_unavailable')
  }

  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string; code?: string }
    const code = err.code ?? (res.status === 429 ? 'RATE_LIMITED' : res.status >= 500 ? 'sso_unavailable' : 'sso_invalid')
    throw new SsoLoginError(err.detail ?? 'Accesso con Equal PSW non riuscito.', code)
  }

  return (await res.json()) as LoginResult
}

/**
 * TASK-0044: session detection on mount. Calls GET /api/auth/me, which reads the current
 * server-side session (Redis-backed since TASK-0037, real 8h sliding idle timeout) rather than
 * anything client-stored. Returns the current user on 200 (same shape as login()'s response), or
 * null on 401 — an absent/expired session is the ordinary, expected first-load state for a
 * logged-out visitor, not an error, so it does NOT throw for that case the way login() throws on
 * bad credentials. Any other non-2xx response IS treated as an error, since that would signal a
 * real backend problem rather than "no session".
 */
export async function fetchMe(): Promise<LoginResponse | null> {
  const res = await fetch('/api/auth/me', { credentials: 'include' })

  if (res.status === 401) return null

  if (!res.ok) {
    throw new Error(`Failed to fetch current session (${res.status})`)
  }

  return (await res.json()) as LoginResponse
}

export async function logout(): Promise<void> {
  // Equal PSW: dopo un logout esplicito il login non rimanda da solo al vault (si rientrerebbe subito).
  markLoggedOut()
  // F-141 (REQ-041-001): le bozze automatiche (localStorage dalla decisione S4) finiscono con la sessione di lavoro.
  try {
    for (const store of [localStorage, sessionStorage]) {
      for (const key of Object.keys(store)) {
        if (key.startsWith('crm.contratti.bozza.')) store.removeItem(key)
      }
    }
  } catch {
    /* storage non disponibile: nessuna bozza da togliere */
  }
  const csrfToken = await fetchCsrfToken()
  await fetch('/api/auth/logout', {
    method: 'POST',
    headers: { 'X-XSRF-TOKEN': csrfToken },
    credentials: 'include',
  })
}
